import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import type { Project, Person, AiProjectSummary, Assignment } from "../services/api";
import { listProjects, listPeople, createAssignment, listAssignments } from "../services/api";
import { Modal } from "./Modal";
import { getErrorAlertMessage } from "../utils/errors";
import type { AlertState } from "../utils/errors";
import { useAuth } from "../auth/AuthContext";

const EXCLUDED_DETAIL_KEYS = new Set(["PK", "SK", "entity_type", "GSI1PK", "GSI1SK", "GSI2PK", "GSI2SK", "GSI3PK", "GSI3SK"]);

export function JobBoardPage() {
  const { user, hasRole } = useAuth();

  const userEmail = (user?.email ?? user?.username ?? "").toLowerCase();
  const userDisplayName = user?.email ?? user?.username ?? "Consultant";
  const isRoot = hasRole("root");
  const isAdminRole = hasRole("admin");
  const isOpsRole = hasRole("ops");
  const canNominateOthers = isRoot || isAdminRole || isOpsRole;

  const [projects, setProjects] = useState<Project[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<string>(canNominateOthers ? "" : userEmail);
  const [submissionAlert, setSubmissionAlert] = useState<AlertState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [archetypeFilter, setArchetypeFilter] = useState<string>("");
  const [skillsFilter, setSkillsFilter] = useState<string>("");

  useEffect(() => {
    void refreshData();
  }, []);

  async function refreshData() {
    setLoading(true);
    setError(null);
    try {
      const [projectData, peopleData, assignmentData] = await Promise.all([listProjects(), listPeople(), listAssignments()]);
      setProjects(projectData);
      setPeople(peopleData);
      setAssignments(assignmentData);
    } catch (err) {
      setError(getErrorAlertMessage("load job board data", err));
    } finally {
      setLoading(false);
    }
  }

  function openProject(project: Project) {
    setActiveProject(project);
    setSelectedPerson(canNominateOthers ? "" : userEmail);
    setSubmissionAlert(null);
  }

  function closeModal() {
    setActiveProject(null);
    setSelectedPerson(canNominateOthers ? "" : userEmail);
    setSubmissionAlert(null);
  }

  async function handleNominate(event: FormEvent) {
    event.preventDefault();
    if (!activeProject) {
      setSubmissionAlert({ type: "error", message: "No requisition selected." });
      return;
    }
    const nomineeEmailRaw = (canNominateOthers ? selectedPerson : userEmail).trim().toLowerCase();
    if (!nomineeEmailRaw) {
      setSubmissionAlert({ type: "error", message: "Please choose a consultant before submitting." });
      return;
    }
    const nomineeEmail = nomineeEmailRaw;
    if (!nomineeEmail) {
      setSubmissionAlert({ type: "error", message: "Please choose a consultant before submitting." });
      return;
    }
    const alreadyAssigned = assignments.some(
      (assignment) => assignment.project_id === activeProject.project_id && assignment.person_email.toLowerCase() === nomineeEmail,
    );
    if (alreadyAssigned) {
      setSubmissionAlert({ type: "error", message: "This consultant is already nominated for this requisition." });
      return;
    }
    setIsSubmitting(true);
    setSubmissionAlert(null);
    try {
      const created = await createAssignment({
        project_id: activeProject.project_id,
        person_email: nomineeEmail,
        status: canNominateOthers ? "NOMINATED" : "SELF_NOMINATED",
      });
      setAssignments((prev) => [...prev, created]);
      setSubmissionAlert({ type: "info", message: "Nomination submitted successfully." });
    } catch (err) {
      setSubmissionAlert({ type: "error", message: getErrorAlertMessage("nominate for this requisition", err) });
    } finally {
      setIsSubmitting(false);
    }
  }

  const archetypeOptions = useMemo(() => {
    const unique = new Set<string>();
    projects.forEach((project) => {
      const summary = project.summary as AiProjectSummary | undefined;
      const archetype = (summary?.tw_archetype ?? project.tw_archetype ?? "").trim();
      if (archetype) {
        unique.add(archetype);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const sortedProjects = useMemo(() => {
    const normalizedSkillsFilter = skillsFilter.trim().toLowerCase();
    const filtered = projects.filter((project) => {
      const summary = project.summary as AiProjectSummary | undefined;
      const archetype = (summary?.tw_archetype ?? project.tw_archetype ?? "").trim();
      if (archetypeFilter && archetype !== archetypeFilter) {
        return false;
      }
      const status = (project.status ?? "").trim().toUpperCase();
      if (status === "EXPIRED") {
        return false;
      }
      if (!canNominateOthers && status === "ACCEPTED") {
        return false;
      }

      if (!normalizedSkillsFilter) {
        return true;
      }

      const skills = [
        ...(summary?.estimated_skills ?? []),
        ...(project.estimated_skills ?? []),
      ];

      if (skills.length === 0) {
        return false;
      }

      return skills.some((skill) => skill.toLowerCase().includes(normalizedSkillsFilter));
    });

    return filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [projects, archetypeFilter, skillsFilter]);

  const sortedPeople = useMemo(() => {
    return [...people].sort((a, b) => a.name.localeCompare(b.name));
  }, [people]);

  const nominationPeople = useMemo(() => {
    if (canNominateOthers) {
      return sortedPeople;
    }
    return sortedPeople.filter((person) => person.email.toLowerCase() === userEmail);
  }, [canNominateOthers, sortedPeople, userEmail]);

  const canSubmitNomination = canNominateOthers ? Boolean(selectedPerson) : Boolean(userEmail);

  function formatDetailValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (value === null || value === undefined) {
      return "—";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  }

  const projectDetails = useMemo(() => {
    if (!activeProject) {
      return [];
    }
    const excludedKeys = new Set([
      ...EXCLUDED_DETAIL_KEYS,
      "tw_archetype",
      "end_date",
      "fte_allocation",
      "sales_funnel_adjustment",
      "tw_grade",
      "week_start",
      "tw_role",
      "priority",
      "title",
      "project_id",
    ]);
    return Object.entries(activeProject)
      .filter(([key, value]) => value !== undefined && !excludedKeys.has(key))
      .map(([key, value]) => ({
        key,
        label: key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
        value: formatDetailValue(value),
      }));
  }, [activeProject]);

  return (
    <div className="job-board">
      <header className="panel">
        <div className="panel-header">
          <h2>Job Board</h2>
          <button type="button" className="ghost" onClick={() => void refreshData()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="form-grid">
          <label>
            TW Archetype
          <select value={archetypeFilter} onChange={(event) => setArchetypeFilter(event.target.value)}>
            <option value="">All archetypes</option>
            {archetypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="span-2">
          Estimated Skills
          <input
            type="search"
            value={skillsFilter}
            onChange={(event) => setSkillsFilter(event.target.value)}
            placeholder="Filter by skill keywords"
          />
        </label>
      </div>
      {error && <div className="alert error">{error}</div>}
    </header>

      {loading ? (
        <div className="panel">Loading requisitions…</div>
      ) : sortedProjects.length === 0 ? (
        <div className="panel">No requisitions available at the moment.</div>
      ) : (
        <div className="job-card-grid">
          {sortedProjects.map((project) => {
            const summary = project.summary as AiProjectSummary | undefined;
            const cardNotes = summary?.notes ?? project.notes ?? project.description ?? "No additional context provided yet.";
            return (
            <button
              key={project.project_id}
              type="button"
              className="job-card"
              onClick={() => openProject(project)}
            >
              <header>
                <h3>{project.title || "Untitled Requisition"}</h3>
                <span className="job-id">{project.project_id}</span>
              </header>
              <div className="job-card-body">
                <p className="job-meta">{project.delivery_unit || "Delivery unit TBC"}</p>
                <p className="job-notes">{cardNotes}</p>
              </div>
              <footer>
                <span
                  className={`status-pill status-${(project.status || "unknown")
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {project.status || "UNKNOWN"}
                </span>
              </footer>
            </button>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(activeProject)}
        onClose={closeModal}
        title={activeProject ? activeProject.project_id : undefined}
      >
        {activeProject && (
          <div className="job-modal">
            <section className="job-details">
              <header className="modal-header" style={{ marginTop: 0, paddingTop: "0.5rem" }}>
                <h3 style={{ margin: 0, textAlign: "center" }}>{activeProject.title || "Untitled Requisition"}</h3>
              </header>
              <div className="details-grid">
                {projectDetails.map((detail) => (
                  <div key={detail.key} className="detail-row">
                    <span className="detail-label">{detail.label}</span>
                    <span className="detail-value">{detail.value}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="job-nomination">
              <h4>{canNominateOthers ? "Nominate Consultant" : "Self Nominate"}</h4>
              <form className="form-grid" onSubmit={handleNominate}>
                <label className="span-2">
                  {canNominateOthers ? "Select Consultant" : "Consultant"}
                  {canNominateOthers ? (
                    <select value={selectedPerson} onChange={(event) => setSelectedPerson(event.target.value)} required>
                      <option value="">Choose a consultant…</option>
                      {nominationPeople.map((person) => (
                        <option key={person.email} value={person.email}>
                          {person.name} ({person.email})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={userDisplayName} disabled />
                  )}
                </label>
                <div className="form-actions span-2">
                  <button type="submit" className="primary" disabled={isSubmitting || !canSubmitNomination}>
                    {isSubmitting ? "Submitting…" : "Submit Nomination"}
                  </button>
                  <button type="button" className="ghost" onClick={closeModal} disabled={isSubmitting}>
                    Close
                  </button>
                </div>
              </form>
              {submissionAlert && <div className={`alert ${submissionAlert.type}`}>{submissionAlert.message}</div>}
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
}
